import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MentorsSelect } from "@/components/dashboard/mentors-select";
import { cn } from "@/lib/utils";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { MenteesSelect } from "./mentees-select";

interface FiltersProps {
  mentorId: string;
  menteeId: string;
  source: string;
  limit: number;
  startDate?: Date;
  endDate?: Date;
  handleFilterChange: (name: string, value: string) => void;
  handleDateChange: (type: "start" | "end", date: Date | undefined) => void;
}

export function Filters({
  mentorId,
  menteeId,
  source,
  limit,
  startDate,
  endDate,
  handleFilterChange,
  handleDateChange,
}: FiltersProps) {
  const handleQuickFilter = (days: number | null) => {
    if (days === null) {
      // All time
      handleDateChange("start", undefined);
      handleDateChange("end", undefined);
    } else {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - days);
      handleDateChange("start", start);
      handleDateChange("end", end);
    }
  };

  return (
    <div className="space-y-4">
      {/* Date Range */}
      <div className="flex flex-col gap-2">
        <Label>Date Range</Label>
        <div className="flex gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "justify-start text-left font-normal",
                  !startDate && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate ? format(startDate, "PPP") : "Start date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => handleDateChange("start", date)}
              />
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "justify-start text-left font-normal",
                  !endDate && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {endDate ? format(endDate, "PPP") : "End date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={(date) => handleDateChange("end", date)}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Quick filters */}
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleQuickFilter(7)}
          >
            Last 7 Days
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleQuickFilter(30)}
          >
            Last 30 Days
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleQuickFilter(null)}
          >
            All Time
          </Button>
        </div>
      </div>

      {/* Mentor & Mentee Select */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <Label>Mentor</Label>
          <MentorsSelect
            mentorId={mentorId || null}
            handleFilterChange={handleFilterChange}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label>Teen</Label>
          <MenteesSelect
            menteeId={menteeId || null}
            handleFilterChange={handleFilterChange}
          />
        </div>
      </div>

      {/* Source Badge Selection */}
      <div className="flex flex-col gap-2">
        <Label>Message Source</Label>
        <div className="flex gap-2">
          <Badge
            variant={source === "all" ? "secondary" : "outline"}
            className={cn("cursor-pointer px-4 py-2", {
              "bg-black text-white hover:bg-black": source === "all",
            })}
            onClick={() => handleFilterChange("source", "all")}
          >
            All Messages
          </Badge>
          <Badge
            variant={source === "mentee" ? "secondary" : "outline"}
            className={cn("cursor-pointer px-4 py-2", {
              "bg-black text-white hover:bg-black": source === "mentee",
            })}
            onClick={() => handleFilterChange("source", "mentee")}
          >
            Teen Only
          </Badge>
          <Badge
            variant={source === "mentor" ? "secondary" : "outline"}
            className={cn("cursor-pointer px-4 py-2", {
              "bg-black text-white hover:bg-black": source === "mentor",
            })}
            onClick={() => handleFilterChange("source", "mentor")}
          >
            Mentor Only
          </Badge>
        </div>
      </div>

      {/* Word Limit Select */}
      <div className="flex flex-col gap-2">
        <Label>Number of Words</Label>
        <Select
          value={limit.toString()}
          onValueChange={(value) => handleFilterChange("limit", value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select word count" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="25">25 words</SelectItem>
            <SelectItem value="50">50 words</SelectItem>
            <SelectItem value="100">100 words</SelectItem>
            <SelectItem value="150">150 words</SelectItem>
            <SelectItem value="200">200 words</SelectItem>
            <SelectItem value="300">300 words</SelectItem>
            <SelectItem value="500">500 words</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
